import React, { useRef } from 'react';
import { getSubNationalLabel, requiresPolicyType, supportsSubNational } from 'services/subNationalService';
import {policyImplementationStatusOptions} from "../../../common/SelectOptions/policyImplementationStatusOptions";

const PreviewField = ({ label, value }) => (
  <div className="border-b border-gray-100 py-3">
    <div className="grid grid-cols-2">
      <div className="text-gray-600">{label}</div>
      <div className="font-normal text-gray-900">
        {label === 'Milestones completed in stage' ? 
          value?.map((value,i)=> <div key={i}>- {value}</div>): 
        <div>{label === 'Systems' ? value?.join(', ') : (value || '-')}</div>}
      </div>
    </div>
  </div>
);

const PolicyPreview = ({ 
    countryOptions,
    systemOptions,
    milestoneOptions,
    formData, 
    policyFile,
    setCurrentStep,
    createPolicy,
    isEditMode,
    policyTypeOptions,
    subNationalOptions,
    isAGRAUser
}) => {
  // Determine if country uses policy scope
  const countryId = formData.policy_request_body.country_id;
  const isSubnational = formData.policy_request_body.policy_scope === "Sub-national";
  
  // Get country name
  const countryName = useRef(countryOptions.find(country => country.value == countryId)?.label || '');
  
  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-6 mt-5">
      <h1 className="text-2xl font-medium mb-8">Preview</h1>
      
      {/* Policy Details Section */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-green-800 mb-4">1. Policy Details</h2>
        <div className="space-y-0">
          <PreviewField 
            label="Policy name" 
            value={formData.policy_request_body.name}
          />
          <PreviewField 
            label="Country" 
            value={countryName.current} 
          />
          
          {supportsSubNational(countryId) && (
            <PreviewField 
              label="Policy Scope" 
              value={formData.policy_request_body.policy_scope} 
            />
          )}
          
          {supportsSubNational(countryId) && isSubnational && (
            <PreviewField 
              label={getSubNationalLabel(countryId)} 
              value={subNationalOptions.find(subNationalOption=> subNationalOption.value == formData.policy_request_body.sub_national_id)?.label}
            />
          )}
          
          {requiresPolicyType(countryId) && (
            <PreviewField 
              label="Policy Type" 
              value={policyTypeOptions.find(policyType => policyType.value == formData.policy_request_body.policy_type_id)?.label}
            />
          )}

          <PreviewField
            label="Implementation Status"
            value={policyImplementationStatusOptions.find(opt => opt.label === formData.policy_request_body.implementation_status)?.label}
          />
          <PreviewField
            label="Publication Year"
            value={formData.policy_request_body.publication_year || '-'}
          />
          <PreviewField
            label="Registration Number"
            value={formData.policy_request_body.registration_number || '-'}
          />

          <PreviewField 
            label="Stage" 
            value={formData.policy_request_body?.stage_id} 
          />
          
          {!isEditMode && !formData.policy_request_body.is_complete && <PreviewField 
            label="Milestones completed in stage" 
            value={formData.current_stage_completed_milestones_ids?.map(milestoneID => milestoneOptions.find(milestone=>milestone.value == milestoneID.milestone_id)?.label)} 
          />}
          
          <PreviewField 
            label="Systems" 
            value={formData.system_ids?.map(systemID => systemOptions.find(system=>system.value == systemID.id)?.label)} 
          />
          
          {!isEditMode && <PreviewField 
            label="Is policy complete?" 
            value={formData.policy_request_body.is_complete ? 'Yes' : 'No'} 
          />}
          
          <PreviewField 
            label="Is policy off track?" 
            value={formData.policy_request_body.is_off_track ? 'Yes' : 'No'} 
          />
          
          <PreviewField 
            label="Is policy delayed?" 
            value={formData.policy_request_body.is_delayed ? 'Yes' : 'No'} 
          />

          <PreviewField 
            label="Does policy support flagship?" 
            value={formData.policy_request_body.supports_flagship ? 'Yes' : 'No'} 
          />
          
          {isAGRAUser && 
            <>
              <PreviewField 
                label="Is AGRA supported?" 
                value={formData.policy_request_body.is_agra_supported ? 'Yes' : 'No'} 
              />
              <PreviewField 
                label="Strategy Version" 
                value={formData.policy_request_body.agra_strategy_version} 
              />
            </>
          }
        </div>
      </div>

      {/* Details Section */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-green-800 mb-4">2. More Details</h2>
        <div className="space-y-4">
          <div className='flex items-center'>
            <div className="text-gray-600 mb-2 text-nowrap mr-1">Policy details</div>
            <div className="bg-gray-50 p-4 rounded w-100">
              {formData.policy_request_body.details}
            </div>
          </div>

          <div className='flex items-center'>
            <div className="text-gray-600 mb-2 text-nowrap mr-1">Policy File</div>
            <div className="bg-gray-50 p-4 rounded w-100">
              {policyFile ? policyFile.name : '-'}
            </div>
          </div>
        </div>
      </div>

      {/* Partner Detail Section */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-green-800 mb-4">3. Partner Detail</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="text-left py-3 text-gray-600 font-medium border-b border-gray-200 px-0">Short name</th>
                <th className="text-left py-3 text-gray-600 font-medium border-b border-gray-200 px-0">Long name</th>
                <th className="text-left py-3 text-gray-600 font-medium border-b border-gray-200 px-0">Technical partner</th>
              </tr>
            </thead>
            <tbody>
              {formData.partner_request_bodies.map((partner, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-3">{partner.short_name || '-'}</td>
                  <td className="py-3">{partner.long_name}</td>
                  <td className="py-3">{partner.is_technical_expert ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        <button
          onClick={()=>setCurrentStep(1)}
          className="px-6 py-2 border rounded text-green-800 hover:bg-gray-50 flex items-center"
        >
          Edit
        </button>
        <button
          onClick={createPolicy}
          className="px-6 py-2 bg-green-100 text-green-800 rounded hover:bg-green-200"
        >
          Save Policy
        </button>
      </div>
    </div>
  );
};

export default PolicyPreview;